import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Lock, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui";
import { platformChangePassword, platformLogout } from "@/auth/api";
import { ApiError } from "@/lib/api";
import { AuthShell } from "@/pages/login/AuthShell";
import { SetupBrand } from "@/pages/login/SetupBrand";
import { PlatformSetupLockup } from "@/pages/login/PlatformBrand";
import { PasswordField } from "@/pages/login/fields";
import { PasswordStrength } from "@/pages/login/PasswordStrength";
import { FormAlert } from "@/pages/login/FormAlert";

// Minimum matches the backend floor (tenancy.minPlatformPasswordLen); kept in
// sync so the client rejects a too-short password before the round trip.
const MIN_LEN = 12;

// PlatformChangePassword is the forced first-login step for a boot-seeded super
// admin (6.6): the console is blocked (by PlatformProtectedRoute) until the
// single-use SUPERADMIN_PASSWORD is replaced. On success it calls onDone, which
// re-checks the account status (flag now clear) and opens the console. The panel
// is the purple platform-setup axis; the break-glass password is re-entered
// because the backend verifies it (ChangeOwnPassword → CheckPassword).
export function PlatformChangePassword({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < MIN_LEN) {
      setError(t("platformPassword.tooShort", { min: MIN_LEN }));
      return;
    }
    if (next !== confirm) {
      setError(t("platformPassword.mismatch"));
      return;
    }
    if (next === current) {
      setError(t("platformPassword.same"));
      return;
    }
    setBusy(true);
    try {
      await platformChangePassword(current, next);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("platformPassword.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      brand={
        <SetupBrand
          accent="platform"
          activeStep={1}
          eyebrow="platformPassword.eyebrow"
          headline="platformPassword.brandHeadline"
          para="platformPassword.brandPara"
          footer="platformPassword.brandFooter"
          lockup={<PlatformSetupLockup />}
        />
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-5.5">
        <div className="flex flex-col gap-3">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-platform-border bg-platform-bg px-3 py-1.5 font-mono text-[11px] tracking-wider text-platform-ink">
            <ShieldAlert size={13} /> {t("platformPassword.badge")}
          </span>
          <div className="flex flex-col gap-1.5">
            <h2 className="font-head text-[28px] font-bold text-ink">
              {t("platformPassword.cardTitle")}
            </h2>
            <p className="text-sm leading-relaxed text-muted">
              {t("platformPassword.cardSubtitle")}
            </p>
          </div>
        </div>

        <FormAlert message={error} />

        <PasswordField
          label={t("platformPassword.current")}
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="••••••••"
        />
        <PasswordField
          label={t("platformPassword.new")}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="••••••••••••"
        />
        <PasswordField
          label={t("platformPassword.confirm")}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="••••••••••••"
        />

        <PasswordStrength password={next} />

        <Button
          type="submit"
          variant="platform"
          fullWidth
          loading={busy}
          disabled={!current || !next || !confirm}
          rightIcon={<ArrowRight size={18} />}
        >
          {t("platformPassword.continue")}
        </Button>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-2">
          <Lock size={14} />
          <span className="text-center">{t("platformPassword.footNote")}</span>
        </div>

        <button
          type="button"
          onClick={() => void platformLogout().then(() => onDone())}
          className="mx-auto text-[13px] font-semibold text-platform-ink hover:underline"
        >
          {t("platformPassword.signOut")}
        </button>
      </form>
    </AuthShell>
  );
}
